import { funcEncryptData, funcDecryptData } from '@/utils/crypt';
import { PART_TYPES, partTypeNames, partTypesMappingToOld } from '@/utils/partTypes';
import { AmplifyToken } from '@/utils/amplify/amplify-token';
import { Bike, Part, Firmware, PartSpec, Customer, MessageGateway } from '@/api/hst-api';
import { compressData } from '@/utils/compressData';
import axios from 'axios';

export const getPartBrandPrefix = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();
  let brandPrefix = '';

  try {
    const partSpec = await new PartSpec(token).getPartSpec(decryptData.PartNo);
    const customer = await new Customer(token).getCustomer(partSpec.result.customer_id);
    brandPrefix = customer.result.short_name;
  } catch {}

  const result = await funcEncryptData({
    ErrorCode: 0,
    BrandPrefix: brandPrefix
  });

  return result;
};

export const bindBikeId = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();

  const equippedParts = await Object.values(partTypeNames).filter(partType => {
    if (`${partType}MID` in decryptData) {
      return decryptData[`${partType}MID`].length > 0 && decryptData[`${partType}PN`].length > 0;
    } else {
      return false;
    }
  }).map((partType) => {
    return {
      'mid': decryptData[`${partType}MID`],
      'part_num': decryptData[`${partType}PN`]
    };
  });

  const parts = await Promise.all(
    equippedParts.map(async part => {
      try {
        await new Part(token).getPart(part.mid);
      } catch (err) {
        return (err.response.status === 404) ? part : err;
      }
    })
  );

  await Promise.all(
    parts.filter(part => part).map(async part => {
      const result = await new Part(token).createPartOutsource({
        id: part.mid,
        partNum: part.part_num,
        barcode: null,
        fwVer: ''
      });

      return result;
    })
  );

  let result = {};
  try {
    const bikeResult = await new Bike(token).createBikeForQwic({
      vin: decryptData.BikeFID || '',
      parts: equippedParts,
      spec_id: null
    });

    result = {
      ErrorCode: 0,
      NewBikeId: bikeResult.result.id,
      frameNumber: bikeResult.result.vin
    };
  } catch (error) {
    let errorCode = error.response.status;
    let message = error.response.body.message;
    if (errorCode === 400 && message === 'Validation error: Bike part configuration abnormal.') {
      errorCode = 10013;
    }
    result = {
      ErrorCode: errorCode,
      ErrorMessage: message
    };
  }

  return funcEncryptData(result);
};

export const getBikeModel = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();
  let bikeModelName = null;

  try {
    const bikeModel = await new Bike(token).getQwicBikeModel(decryptData.FrameNo);
    bikeModelName = bikeModel.result.qwic_bike_model;
  } catch {}

  const result = await funcEncryptData({
    ErrorCode: 0,
    BikeModel: bikeModelName
  });

  return result;
};

export const checkInvalidFrame = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();
  let invalidFrame = false;
  let errorCode = 0;

  try {
    const bikeModel = await new Bike(token).getQwicBikeModel(decryptData.FrameNo);
    invalidFrame = !(bikeModel.result.valid_frame_number === 'true' || false);
  }
  catch (error) {
    errorCode = error.response ? error.response.status : 10014;
  }

  const result = await funcEncryptData({
    ErrorCode: errorCode,
    InvalidFrame: invalidFrame
  });

  return result;
}

export const getBikeLatestFW = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();
  const filterPartNoList = decryptData.PartNoList.filter(partNum => partNum);
  const latestFirmwareVerResult = await Promise.all(filterPartNoList.map(partNum => new Firmware(token).queryFirmwareList(partNum)));
  const partSpecs = await Promise.all(filterPartNoList.map(partNum => new PartSpec(token).getPartSpec(partNum).catch(e => false)));
  const latestFirmwareVer = await latestFirmwareVerResult.reduce((result, item) => {
    if (item.result.length > 0) {
      result[item.result[0].part_num] = item.result[0];
    } else {
      result[''] = false;
    }
    return result;
  }, {});
  const firmwareList = await partSpecs.filter(partSpec => partSpec).map(partSpec => {
    const partSpecResult = partSpec.result;
    const firmwareResult = latestFirmwareVer[partSpecResult.id];
    const partType = partSpecResult.type;
    let result = {
      'PartType': partTypesMappingToOld[partType],
      'Part': partTypeNames[partType],
      'PartName': partSpecResult.name,
      'LatestFW': '--',
      'HasData': false,
      'FirmwareId': 0
    };
    if (firmwareResult) {
      Object.assign(result, {
        'LatestFW': firmwareResult.fw_ver,
        'HasData': true,
        'FirmwareId': firmwareResult.id
      });
    }

    return result;
  });
  const result = await funcEncryptData({
    'ErrorCode': 0,
    'FwList': firmwareList
  });

  return result;
};

export const getPartFWData = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();

  // part type Motor has different files
  const firmwareResult = await new Firmware(token).queryFirmwareList(decryptData.PartNo);
  let result = {
    'ErrorCode': 0,
    'FWData': '',
    'FWChecksum': '',
    'FWParametersData': '',
    'FWParametersChecksum': '',
    'FWDataAppFile': ''
  };
  const firmwareIds = firmwareResult.result.filter(dt => dt.part_num === decryptData.PartNo);
  if (firmwareIds.length > 0) {
    const firmwareId = firmwareIds[0].id;
    const [fileUrlsResult, partSpecResult] = await Promise.all([
      new Firmware(token).getFirmwareUrl(firmwareId),
      new PartSpec(token).getPartSpec(decryptData.PartNo)
    ]);
    const filter = (partSpecResult.result.type === PART_TYPES.MOTOR) ? { 'srec': '', 'csv': 'Parameters' } : { 'hex': '' };
    const fileUrls = fileUrlsResult.result.filter(firmware => firmware.type in filter);

    const partSpecPromises = await fileUrls.map(fileUrl => axios.get(fileUrl.signed_url));
    const partSpecs = await Promise.all(partSpecPromises);

    await fileUrls.map((fileUrl, index) => {
      const fileContent = partSpecs[index].data;
      const [outputBase64, md5] = compressData(fileContent);

      if (fileUrl.checksum !== md5) {
        return false;
      }

      Object.assign(result, {
        [`FW${filter[fileUrl.type]}Data`]: outputBase64,
        [`FW${filter[fileUrl.type]}Checksum`]: md5
      });
    });
  }

  return funcEncryptData(result);
};

export const getNewMID = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();
  let newMid = '';

  try {
    const part = await new Part(token).createPart({
      partNum: decryptData,
      barcode: null,
      fwVer: ''
    });
    newMid = part.result.id;
  } catch {}

  const result = await funcEncryptData({
    ErrorCode: 0,
    NewMID: newMid
  });

  return result;
};

export const getMGVersion = async data => {
  const token = await AmplifyToken.getToken();

  const messageGateways = await new MessageGateway(token).getLatestVersion();
  const result = await funcEncryptData({
    NewVersion: messageGateways.result.new_version,
    DownloadUrl: messageGateways.result.download_url
  });

  return result;
};

export const compareBikeParts = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();

  const equippedParts = await await Object.values(partTypeNames).filter(partType => {
    if (`${partType}MID` in decryptData) {
      return decryptData[`${partType}MID`].length > 0 && decryptData[`${partType}PN`].length > 0;
    } else {
      return false;
    }
  }).map((partType) => {
    return {
      'mid': decryptData[`${partType}MID`],
      'part_num': decryptData[`${partType}PN`]
    };
  });

  const bikeResult = await new Bike(token).compareQwicBike({
    vin: decryptData.BikeFID,
    parts: equippedParts
  });

  const result = {
    ErrorCode: 0,
    CompareType: bikeResult.result.compare_type,
    VIN: bikeResult.result.current_vin
  };

  return funcEncryptData(result);
};

/**
 * 使用 FrameNo 搜尋時， MID 請給空字串
 * 使用 MID 搜尋時， FrameNo 請給空字串
 * 當兩者皆有值時，將使用 FrameNo 搜尋
 */

export const getBikeInfo = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();

  const bike = new Bike(token);
  const bikeId = await bike.getBikeIdByMid(decryptData.MID);
  const bikeBundles = await bike.getBikeBundleByBikeId(bikeId);
  let bikeVin = '';
  if (bikeId !== '') {
    const bikeInfo = await bike.getBikeInfo(bikeId);
    bikeVin = bikeInfo.vin;
  }

  let result = {
    'ErrorCode': 0,
    'BikeInfo': {
      'BikeID': bikeId,
      'FrameNo': bikeVin,
      'BatteryMID': '',
      'BatteryPN': '',
      'ConsoleMID': '',
      'ConsolePN': '',
      'DisplayMID': '',
      'DisplayPN': '',
      'DriverMID': '',
      'DriverPN': ''
    }
  };

  const partPromises = await bikeBundles.map((bikeBundle) => new Part(token).getPart(bikeBundle.mid));
  const partTypeMap = bikeBundles.reduce((result, item) => {
    result[item.mid] = item.part_type;
    return result;
  }, {});

  const partResult = await Promise.all(partPromises);

  const bikePartsFiltered = bikeBundles.filter(part => [PART_TYPES.MOTOR, PART_TYPES.DRIVER].indexOf(partTypeMap[part.mid]) !== -1);
  const latestPart = bikePartsFiltered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const latestEquippedDriver = (latestPart.length > 0) ? latestPart[0].mid : '';

  partResult.map(part => {
    const partResult = part.result;
    let partType = partTypeNames[partTypeMap[partResult.id]];
    if ([PART_TYPES.MOTOR, PART_TYPES.DRIVER].indexOf(partTypeMap[partResult.id]) !== -1) {
      if (partResult.id === latestEquippedDriver) {
        partType = partTypeNames[PART_TYPES.DRIVER];
      } else {
        return;
      }
    }

    Object.assign(result.BikeInfo, {
      [`${partType}MID`]: partResult.id,
      [`${partType}PN`]: partResult.part_num
    });
  });

  return funcEncryptData(result);
};

// connect 的时候，會先確定是否display, console還是console
export const getConsoleInfo = async data => {
  const decryptData = funcDecryptData(JSON.parse(data));
  const token = await AmplifyToken.getToken();

  let consoleBtns = [];
  let equipPanel = true;
  try {
    const partSpec = await new PartSpec(token).getPartSpec(decryptData.PartNo);
    if (partSpec.result.type !== PART_TYPES.CONSOLE) {
      throw new Error('Part type is not console');
    }
    consoleBtns = Array(6).fill('').map((_, num) => partSpec.result.meta[`button${num}_func`]);
    equipPanel = (partSpec.result.meta.equip_panel === 'Y');
  } catch {}

  const result = {
    ErrorCode: 0,
    IncludeLCM: equipPanel,
    ConsoleBtns: consoleBtns
  };
  return funcEncryptData(result);
};
